import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
	Badge,
	Icon,
	LinkButton,
	objectNestedKeys,
	Popover,
	StatusIndicator,
	Text,
	Tooltip,
	truncateText,
} from '@nicheinc/campus-ds';

import { TableMapperType } from '../../types';

type PropsType = {
	mapperField: TableMapperType;
	item: any;
};

const CellContainer = styled.div`
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.spacing.xs};
	width: 100%;
`;

const TextsContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing.xxs};
	height: 100%;
`;

const LineClampedLinkButton = styled(LinkButton)<{ $lineClamp?: number }>`
	${({ $lineClamp }) =>
		$lineClamp &&
		`
	display: -webkit-box;
	-webkit-line-clamp: ${$lineClamp};
	-webkit-box-orient: vertical;
	overflow: hidden;
	white-space: normal;
	text-overflow: ellipsis;
	word-break: break-word;
  `}
`;

function TextCell({ mapperField, item }: PropsType) {
	//Text fields
	const text = objectNestedKeys(item, mapperField?.key, true);

	const subText = mapperField?.subTextKey
		? typeof mapperField?.subTextKey === 'function'
			? item[mapperField?.subTextKey(item) as string]
			: item[mapperField?.subTextKey]
		: undefined;

	const descriptionText = mapperField?.descriptionTextKey
		? typeof mapperField.descriptionTextKey === 'function'
			? item[mapperField.descriptionTextKey(item) as string]
			: item[mapperField.descriptionTextKey]
		: undefined;

	//Text length settings
	const textRef = useRef<HTMLDivElement | HTMLAnchorElement>(null);
	const subTextRef = useRef<HTMLDivElement>(null);
	const descriptionTextRef = useRef<HTMLDivElement>(null);
	const [textTruncated, setTextTruncated] = useState(false);
	const [subTextTruncated, setSubTextTruncated] = useState(false);
	const [descriptionTextTruncated, setDescriptionTextTruncated] =
		useState(false);

	const textMaxLength = mapperField?.textMaxLength;

	//Text status
	const textStatusIndicatorVariant =
		typeof mapperField.textStatus === 'function'
			? mapperField.textStatus(item)
			: mapperField.textStatus;

	const hasStatus = !!textStatusIndicatorVariant;

	const statusOnCell = mapperField?.textStatusOnCell ?? hasStatus;
	const statusOnText = mapperField?.textStatusOnText ?? hasStatus;
	const statusOnSubText = mapperField?.textStatusOnSubText ?? hasStatus;
	const statusOnDescription = mapperField?.textStatusOnDescription ?? hasStatus;

	//Text icon
	const iconText =
		typeof mapperField.textIcon === 'function'
			? mapperField.textIcon(item)
			: mapperField.textIcon;
	const hasIcon = !!iconText;

	const iconOnCell =
		typeof mapperField.textIconOnCell === 'function'
			? mapperField.textIconOnCell(item)
			: (mapperField?.textIconOnCell ?? hasIcon);
	const iconOnText = mapperField?.textIconOnText ?? hasIcon;
	const iconOnSubText =
		typeof mapperField?.textIconOnSubText === 'function'
			? mapperField.textIconOnSubText(item)
			: (mapperField?.textIconOnSubText ?? hasIcon);
	const iconOnDescription =
		typeof mapperField?.textIconOnDescription === 'function'
			? mapperField.textIconOnDescription(item)
			: (mapperField?.textIconOnDescription ??
				(hasIcon && mapperField?.textOnClick));

	const iconOnLinkButton =
		typeof mapperField?.textIconOnLinkButton === 'function'
			? mapperField.textIconOnLinkButton(item)
			: (mapperField?.textIconOnLinkButton ?? hasIcon);

	const textTooltipText =
		typeof mapperField.textTooltipText === 'function'
			? mapperField.textTooltipText(item)
			: mapperField.textTooltipText;

	const textPopoverContent =
		typeof mapperField?.textPopoverContent === 'function'
			? mapperField?.textPopoverContent(item)
			: mapperField?.textPopoverContent;

	const subTextBadgeData =
		mapperField?.textSubTextBadge &&
		(typeof mapperField.textSubTextBadge === 'function'
			? mapperField.textSubTextBadge(item)
			: mapperField.textSubTextBadge);

	const subTextBadgeVariant =
		typeof subTextBadgeData?.variant === 'function'
			? subTextBadgeData?.variant(item)
			: subTextBadgeData?.variant;

	const subTextBadgeText =
		typeof subTextBadgeData?.text === 'function'
			? subTextBadgeData?.text(item)
			: subTextBadgeData?.text;

	const textColor =
		typeof mapperField.textColor === 'function'
			? mapperField.textColor(item)
			: mapperField.textColor;

	const textIconColor =
		typeof mapperField.textIconColor === 'function'
			? mapperField.textIconColor(item)
			: mapperField.textIconColor || 'system.text.weak';

	const textLineClamp =
		typeof mapperField?.lineClamp === 'function'
			? mapperField?.lineClamp(item)
			: mapperField?.lineClamp || 1;

	const subTextLineClamp =
		typeof mapperField?.subTextLineClamp === 'function'
			? mapperField?.subTextLineClamp(item)
			: mapperField?.subTextLineClamp || 1;

	const descriptionTextLineClamp =
		typeof mapperField?.descriptionTextLineClamp === 'function'
			? mapperField?.descriptionTextLineClamp(item)
			: mapperField?.descriptionTextLineClamp;

	//Get tooltip text
	const getTooltipText = useCallback(() => {
		if (!textTruncated && !subTextTruncated && !descriptionTextTruncated) {
			return '';
		}

		if (textTruncated || subTextTruncated || descriptionTextTruncated) {
			return `${text}${subText ? `\n\n${subText}` : ''}${descriptionText ? `\n\n${descriptionText}` : ''}`;
		}
	}, [
		text,
		subText,
		descriptionText,
		textTruncated,
		subTextTruncated,
		descriptionTextTruncated,
	]);

	useEffect(() => {
		// Check if text is truncated by maxLength config
		if (text && textMaxLength && text.length > textMaxLength) {
			setTextTruncated(true);
		}

		if (subText && textMaxLength && subText.length > textMaxLength) {
			setSubTextTruncated(true);
		}

		if (
			descriptionText &&
			textMaxLength &&
			descriptionText.length > textMaxLength
		) {
			setDescriptionTextTruncated(true);
		}

		const textElement = textRef.current;
		const subTextElement = subTextRef.current;
		const descriptionTextElement = descriptionTextRef.current;

		const checkTruncation = () => {
			// Only check if we haven't already determined it's truncated
			if (!textTruncated && textElement) {
				const isTextElementTruncated =
					textElement.scrollHeight > textElement.clientHeight + 1;
				if (isTextElementTruncated) {
					setTextTruncated(true);
				}
			}

			if (!subTextTruncated && subTextElement) {
				const isSubTextElementTruncated =
					subTextElement.scrollHeight > subTextElement.clientHeight + 1;
				if (isSubTextElementTruncated) {
					setSubTextTruncated(true);
				}
			}

			if (!descriptionTextTruncated && descriptionTextElement) {
				const isDescriptionElementTruncated =
					descriptionTextElement.scrollHeight >
					descriptionTextElement.clientHeight + 1;
				if (isDescriptionElementTruncated) {
					setDescriptionTextTruncated(true);
				}
			}
		};

		// Use requestAnimationFrame to ensure elements are rendered
		if (textElement || subTextElement || descriptionTextElement) {
			requestAnimationFrame(checkTruncation);
		}

		const resizeObserver = new ResizeObserver(() => {
			requestAnimationFrame(checkTruncation);
		});

		if (textElement) resizeObserver.observe(textElement);
		if (subTextElement) resizeObserver.observe(subTextElement);
		if (descriptionTextElement) resizeObserver.observe(descriptionTextElement);

		return () => {
			resizeObserver.disconnect();
		};
	}, [
		text,
		subText,
		descriptionText,
		textMaxLength,
		textTruncated,
		subTextTruncated,
		descriptionTextTruncated,
		textLineClamp,
		subTextLineClamp,
		descriptionTextLineClamp,
	]);

	const Cell = React.forwardRef(function Cell(
		_props,
		ref: React.Ref<HTMLDivElement>,
	) {
		return (
			<CellContainer ref={ref}>
				{hasIcon && iconOnCell && (
					<Icon
						size="xs"
						iconName={iconText as string}
						color={textIconColor}
						weight={
							mapperField.textIconWeight ? mapperField.textIconWeight : 'fill'
						}
					/>
				)}

				{hasStatus && statusOnCell && (
					<StatusIndicator variant={textStatusIndicatorVariant} />
				)}
				<TextsContainer>
					<div className="d-flex align-items-center gap-2 h-100">
						{hasStatus && statusOnText && (
							<StatusIndicator variant={textStatusIndicatorVariant} />
						)}

						{hasIcon && iconOnText && (
							<Icon
								iconName={iconText as string}
								color={textIconColor}
								weight={
									mapperField.textIconWeight
										? mapperField.textIconWeight
										: 'fill'
								}
								size="xs"
							/>
						)}

						{!!text && (
							<>
								{mapperField.textOnClick ? (
									<LineClampedLinkButton
										onClick={() =>
											mapperField.textOnClick && mapperField.textOnClick(item)
										}
										lineHeight="medium"
										weight={mapperField.textTitleFontWeight || 'regular'}
										color={textColor}
										icon={hasIcon && iconOnLinkButton ? iconText : undefined}
										$lineClamp={textLineClamp}
										ref={textRef as React.Ref<HTMLAnchorElement>}
									>
										{textLineClamp && !textMaxLength
											? text
											: textMaxLength
												? truncateText(text, textMaxLength)
												: text}
									</LineClampedLinkButton>
								) : (
									<Text
										size="small"
										lineHeight="medium"
										fontWeight={mapperField?.textTitleFontWeight || 'regular'}
										lineClamp={textLineClamp}
										ref={textRef}
										preserveWhiteSpace
									>
										{textLineClamp && !textMaxLength
											? text
											: textMaxLength
												? truncateText(text, textMaxLength)
												: text}
									</Text>
								)}
							</>
						)}
					</div>

					{!!subText && (
						<div className="d-flex align-items-center gap-2 w-100">
							{hasStatus && statusOnSubText && (
								<StatusIndicator variant={textStatusIndicatorVariant} />
							)}
							{hasIcon && iconOnSubText && (
								<Icon
									iconName={iconText as string}
									color={textIconColor}
									weight={
										mapperField.textIconWeight
											? mapperField.textIconWeight
											: 'fill'
									}
									size="xs"
								/>
							)}
							<Text
								size="small"
								color="system.text.medium"
								lineHeight="medium"
								fontWeight={mapperField.bold ? 'bold' : 'regular'}
								lineClamp={subTextLineClamp}
								ref={subTextRef}
								preserveWhiteSpace
							>
								{subTextLineClamp && !textMaxLength
									? subText
									: textMaxLength
										? truncateText(subText, textMaxLength)
										: subText}
							</Text>
							{subTextBadgeData && (
								<Badge
									variant={subTextBadgeVariant}
									text={subTextBadgeText}
									size="small"
								/>
							)}
						</div>
					)}

					{!!descriptionText && (
						<div className="d-flex align-items-center gap-2">
							{hasStatus && statusOnDescription && (
								<StatusIndicator variant={textStatusIndicatorVariant} />
							)}

							{hasIcon && iconOnDescription && (
								<Icon
									iconName={iconText as string}
									color={textIconColor}
									weight="fill"
									size="xs"
								/>
							)}

							<Text
								size="xs"
								color="system.text.medium"
								lineHeight="medium"
								fontWeight={mapperField.bold ? 'bold' : 'regular'}
								lineClamp={descriptionTextLineClamp}
								ref={descriptionTextRef}
								preserveWhiteSpace
							>
								{descriptionTextLineClamp && !textMaxLength
									? descriptionText
									: textMaxLength
										? truncateText(descriptionText, textMaxLength)
										: descriptionText}
							</Text>
						</div>
					)}
				</TextsContainer>
			</CellContainer>
		);
	});

	if (textPopoverContent) {
		return (
			<Popover
				openOnHover
				withArrow
				popOverContent={textPopoverContent}
				openHoverDelay={200}
			>
				<Cell />
			</Popover>
		);
	}

	return (
		<Tooltip content={getTooltipText()}>
			<Cell />
		</Tooltip>
	);
}

export default React.memo(TextCell);
